/**
 * CANLI YAYIN KÖPRÜSÜ — yerel zar atışlarını masaya duyurur.
 *
 * SORU: Zar atışı nerede oluyor? `packages/ui/src/rollStore.ts` içinde, tamamen
 * YEREL. GM'in görmesi için atışın sunucuya gitmesi gerekir. Bu modül o köprüdür.
 *
 * TASARIM KARARLARI
 *
 * 1) YAYINLAMA ASLA ATIŞI ENGELLEMEZ.
 *    Atış önce yerelde gösterilir, SONRA yayınlanır. Yayın çökerse oyuncu
 *    zarını yine görür — sadece GM görmez. Tersi olsaydı (önce yayın, sonra
 *    göster) yavaş bir ağda oyuncu tıklamaya basıp cevap alamazdı.
 *
 * 2) YEREL DEPO MODUNDA YAYIN YAPILMAZ.
 *    `backend === 'local'` ise sunucu yoktur; denemek gereksiz ve sessiz
 *    hatalar üretirdi.
 *
 * 3) ÇEVRİMDIŞINDA SESSİZ.
 *    Zarhane PWA'dır. Uçakta atış yapan oyuncunun zarı kaybolmaz, sadece
 *    masada görünmez. Kullanıcıya hata göstermek yerine bir göstergenin
 *    söylemesi yeterlidir (`TableView` bunu "bağlantı yok" diye yazar).
 *
 * 4) TOHUM YAYINLANIR.
 *    GM ekranında "dogrulanabilir" yazmasının sebebi budur: tohum + sonuç
 *    ikilisi saklanır, masada herkes tekrar üretebilir.
 */

import { type PendingRoll, useRollStore } from '@tttrpg/ui'
import { useEffect, useRef } from 'react'

/** Yayınlanabilir atışın sunucuya giden gövdesi. */
interface RollPayload {
  sessionKey: string
  characterId: string | null
  label: string
  formula: string
  seed: number
  total: number
  breakdown: { sides: number; value: number; kept: boolean }[]
}

/** Atışı yayınlanabilir biçime çevirir. Atışın HANGI formülü attığını bilir. */
function toPayload(roll: PendingRoll, sessionKey: string, characterId: string | null): RollPayload {
  // d20 testleri: atılan zarlar `d20.rolls`, zar sayısı 1 veya 2 (avantaj).
  // Saldırılar: isabet zarı + hasar zarı birlikte.
  const d20Dice = (roll.d20?.rolls ?? []).map((value) => ({ sides: 20, value, kept: true }))
  const formulaDice = roll.formula?.dice ?? []
  const formulaTotal = roll.formula?.total ?? 0

  let total = 0
  let breakdown: RollPayload['breakdown'] = []

  if (roll.kind === 'd20' && roll.d20) {
    total = roll.d20.total
    breakdown = d20Dice
  } else if (roll.kind === 'attack') {
    // Toplam hasar + isabet bonusu raporlanır; masada "kaç vurdu" sorusunun
    // cevabı atışın kendisi, toplam ise `hit` alanında.
    total = roll.attack?.damageTotal ?? 0
    breakdown = [...d20Dice, ...formulaDice]
  } else {
    total = formulaTotal
    breakdown = formulaDice
  }

  return {
    sessionKey,
    characterId,
    label: roll.label,
    formula: roll.subtitle,
    seed: roll.seed,
    total,
    breakdown,
  }
}

export interface PublishBridgeOptions {
  apiBase: string
  /** 'worker' degilse yayin yapilmaz. */
  backend: 'worker' | 'local'
  /** Bu atışın hangi karaktere ait olduğu. null = atışsız (initiative vb.). */
  characterId: string | null
  /** Oturum anahtarı. Aynı seansta aynı değer kullanılmalı. */
  sessionKey?: string
}

export function usePublishRolls(options: PublishBridgeOptions | null): void {
  // Seçenekleri ref'te tut: her render'da yeni nesne gelmesi efekti
  // gereksiz yere yeniden kurmasın.
  const optionsRef = useRef(options)
  optionsRef.current = options

  // Yayınlanan atişların kimligi: ayni atiş iki kez yayinlanmaz.
  const publishedRef = useRef<Set<number>>(new Set())

  useEffect(() => {
    const unsubscribe = useRollStore.subscribe((state) => {
      // Yeni atışlar `history`'ye eklenir. `publishedRef` hangilerinin zaten
      // yayınlandığını bilir; zustand `subscribe` yalnızca durum DEĞİŞTİĞİNDE
      // çağrıldığı için "yeni olan" buradan çıkar.
      const fresh = state.history.filter((roll) => !publishedRef.current.has(roll.id))
      if (fresh.length === 0) return

      const current = optionsRef.current
      // Sunucu yoksa yayın denemesi anlamsız; atışlar yine de yerel geçmişte.
      if (current?.backend !== 'worker') return

      const sessionKey = current.sessionKey ?? 'canli'
      for (const roll of fresh) {
        publishedRef.current.add(roll.id)
        const payload = toPayload(roll, sessionKey, current.characterId)

        // Ateş ve unut: yayın hatası oyun akışını kesmemeli.
        void fetch(`${current.apiBase}/api/table/roll`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).catch(() => {
          // Sessiz: oyuncu zari yerelde gormeye devam ediyor.
        })
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])
}
